/* V167.3 — 5-Day Challenge Admin Tab */
import { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function Stat({ label, value, sub, color = "text-emerald-300" }) {
  return (
    <div className="bg-zinc-950/60 border border-zinc-800 rounded-xl p-4">
      <div className="text-[10px] text-zinc-300 font-mono uppercase tracking-wider mb-1">{label}</div>
      <div className={`text-2xl font-black ${color}`}>{value}</div>
      {sub && <div className="text-[11px] text-zinc-400 mt-1">{sub}</div>}
    </div>
  );
}

export default function AdminChallengeTab() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [newStart, setNewStart] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    const token = localStorage.getItem("admin_token") || "";
    if (!token) { setErr("Admin token chýba."); return; }
    axios.get(`${API}/challenge/admin/stats`, { headers: { "X-Admin-Token": token } })
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "load_failed"));
  };
  useEffect(() => { load(); }, []);

  const updateNextStart = async (isoOrNull) => {
    const token = localStorage.getItem("admin_token") || "";
    setSaving(true);
    try {
      await axios.post(`${API}/challenge/admin/set-next-start`, { iso: isoOrNull },
        { headers: { "X-Admin-Token": token } });
      load();
      setNewStart("");
    } catch (e) {
      setErr(e.response?.data?.detail || "save_failed");
    } finally { setSaving(false); }
  };

  if (err && !data) return <div className="p-6 text-rose-300" data-testid="ch-admin-error">{err}</div>;
  if (!data) return <div className="p-6 text-zinc-500" data-testid="ch-admin-loading">Načítavam…</div>;

  const t = data.totals || {};
  const next = data.next_cohort_start_iso;
  const nextDate = next ? new Date(next) : null;
  const isOverridden = false; // server doesn't tell us — we just show current

  return (
    <div className="p-3 sm:p-6 space-y-6" data-testid="admin-challenge-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl sm:text-2xl font-black text-white">🏁 5-Day Challenge</h2>
          <p className="text-zinc-400 text-sm mt-1">V167.3 — Cohort-based leadgen funnel · /vyzva-5-dni</p>
        </div>
        <button onClick={load} className="px-3 py-1.5 bg-zinc-900 border border-zinc-700 text-zinc-300 text-xs rounded hover:bg-zinc-800">↻ Obnoviť</button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="ch-admin-kpis">
        <Stat label="Total opt-ins" value={(t.total_enrolled || 0).toLocaleString("sk-SK")} color="text-white"/>
        <Stat label="Dnes" value={t.enrolled_today || 0}/>
        <Stat label="Drip emaily naplánované" value={(t.drip_emails_scheduled || 0).toLocaleString("sk-SK")} color="text-amber-300"/>
        <Stat label="Drip emaily odoslané" value={(t.drip_emails_sent || 0).toLocaleString("sk-SK")} color="text-violet-300"/>
        <Stat label="Konverzie → bundle" value={t.converted_to_bundle || 0} color="text-emerald-400"/>
        <Stat label="Conversion %" value={`${t.conversion_rate_pct || 0}%`} sub="opt-in → DIAGNOSTIKA50" color="text-emerald-200"/>
      </div>

      {/* Next cohort */}
      <div className="bg-zinc-950/60 border border-zinc-800 rounded-xl p-5">
        <h3 className="text-sm font-bold text-zinc-200 mb-3">Najbližší cohort</h3>
        <div className="text-2xl font-black text-emerald-300 mb-1">
          {nextDate ? nextDate.toLocaleString("sk-SK", { dateStyle: "full", timeStyle: "short" }) : "—"}
        </div>
        <div className="text-xs text-zinc-500 font-mono mb-4">{next}</div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="datetime-local"
            value={newStart}
            onChange={(e) => setNewStart(e.target.value)}
            className="px-3 py-2 bg-black/40 border border-zinc-700 rounded text-zinc-200 text-sm"
            data-testid="ch-admin-next-start-input"
          />
          <button
            onClick={() => newStart && updateNextStart(new Date(newStart).toISOString())}
            disabled={!newStart || saving}
            className="px-3 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold rounded disabled:opacity-50"
            data-testid="ch-admin-save-next"
          >Nastaviť ručne</button>
          <button
            onClick={() => updateNextStart(null)}
            disabled={saving}
            className="px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-sm rounded disabled:opacity-50"
            data-testid="ch-admin-reset-next"
          >Resetovať na auto (najbližší pondelok 9:00)</button>
        </div>
      </div>

      {/* Cohorts */}
      <div className="bg-zinc-950/60 border border-zinc-800 rounded-xl overflow-hidden">
        <h3 className="text-sm font-bold text-zinc-200 px-4 py-3 border-b border-zinc-800">
          Cohorts (najnovších 12)
        </h3>
        <table className="w-full text-sm">
          <thead className="bg-zinc-900 text-zinc-500 uppercase text-[10px] font-mono">
            <tr>
              <th className="text-left px-4 py-2">Štart</th>
              <th className="text-right px-4 py-2">Účastníkov</th>
            </tr>
          </thead>
          <tbody>
            {data.cohorts.length === 0 && (
              <tr><td colSpan={2} className="text-center text-zinc-500 py-6">Žiadne kohorty zatiaľ.</td></tr>
            )}
            {data.cohorts.map((c) => (
              <tr key={c.cohort_start} className="border-t border-zinc-900">
                <td className="px-4 py-2 text-zinc-300 font-mono">{c.cohort_start.slice(0, 16).replace("T", " ")}</td>
                <td className="px-4 py-2 text-right text-emerald-300 font-bold">{c.enrolled}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Source distribution */}
      <div className="bg-zinc-950/60 border border-zinc-800 rounded-xl p-4">
        <h3 className="text-sm font-bold text-zinc-200 mb-3">Zdroje opt-inov</h3>
        <div className="space-y-1.5">
          {data.sources.length === 0 && <div className="text-zinc-500 text-sm">Žiadne dáta.</div>}
          {data.sources.map((s) => (
            <div key={s.source} className="flex items-center gap-3 text-sm">
              <div className="w-32 text-zinc-300 font-mono text-xs truncate">{s.source}</div>
              <div className="flex-1 h-5 bg-zinc-900 rounded overflow-hidden">
                <div
                  className="h-full bg-emerald-500/60"
                  style={{ width: `${Math.max(2, (s.count / Math.max(1, data.sources[0].count)) * 100)}%` }}
                />
              </div>
              <div className="w-12 text-right text-xs font-mono text-zinc-400">{s.count}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent enrolments */}
      <div className="bg-zinc-950/60 border border-zinc-800 rounded-xl overflow-hidden">
        <h3 className="text-sm font-bold text-zinc-200 px-4 py-3 border-b border-zinc-800">Posledné opt-iny (30)</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-zinc-900 text-zinc-500 uppercase font-mono">
              <tr>
                <th className="text-left px-4 py-2">Kedy</th>
                <th className="text-left px-4 py-2">Email</th>
                <th className="text-left px-4 py-2">Lang</th>
                <th className="text-left px-4 py-2">Source</th>
                <th className="text-left px-4 py-2">Cohort</th>
              </tr>
            </thead>
            <tbody>
              {data.recent.length === 0 && (
                <tr><td colSpan={5} className="text-center text-zinc-500 py-6">Žiadne dáta.</td></tr>
              )}
              {data.recent.map((r, i) => (
                <tr key={i} className="border-t border-zinc-900">
                  <td className="px-4 py-2 text-zinc-500 font-mono whitespace-nowrap">{(r.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-4 py-2 text-zinc-300">{r.email}</td>
                  <td className="px-4 py-2 text-zinc-400 font-mono uppercase">{r.language}</td>
                  <td className="px-4 py-2 text-zinc-400 font-mono">{r.source}</td>
                  <td className="px-4 py-2 text-zinc-500 font-mono">{(r.cohort_start || "").slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
